import { Injectable } from '@angular/core';
import {AngularFirestore} from '@angular/fire/firestore';
import {SessionService} from './session.service';
import {CommonService} from './common.service';
import {MessageService} from './message.service';
import {MasterService} from './master.service';
import {ChumonCSVForIwamura, ChumonForIwamura, ChumonForManualAdd} from '../../shared/interfaces/chumon';
import {OsuMesu, ShiyoulotKey, Shiyuu, yyyyMMdd} from '../../shared/typeAliases';
import firebase from 'firebase';
import {
  COLLECTION_NAME,
  DOCUMENT_NAME,
  MANUAL_CHUMON_PREFIX,
  QUERY_LIMIT_CHUMON,
  SHIYOULOT_KEY_SUFFIX
} from '../../shared/constants';
import firestore = firebase.firestore;
import {firstValueFrom, Observable} from 'rxjs';
import {
  SelectShiyoulotDialogComponent, SelectShiyoulotDialogData,
  SelectShiyoulotDialogResp
} from '../../shared/dialog/select-shiyoulot-dialog/select-shiyoulot-dialog.component';
import {MatDialog} from '@angular/material/dialog';
import {HimodukiLotInfo, ShiyoulotV2} from '../../shared/interfaces/shiyoulot';
import {TreeNode} from '../../chumon/himoduki-lot-tree/himoduki-lot-tree.component';
import {ShiyoulotService} from './shiyoulot.service';
import {ShukkaEvent} from '../../shared/interfaces/shukka-event';
import {ShukkaSaki} from '../../shared/interfaces/shukkasaki';
import {DocNumberKanriService} from "./doc-number-kanri.service";

export interface ChumonKensakuKey {
  id?: string;
  chumonNumber?: number;
  shukkaSakiId?: string;
  keishuId?: string;
  edukeDate?: yyyyMMdd;
  yoteiShukkaDateFrom?: yyyyMMdd;
  yoteiShukkaDateTo?: yyyyMMdd;
  tantousha?: string;
}

@Injectable({
  providedIn: 'root'
})
export class ChumonService {
  selectedChumon?: ChumonForIwamura;
  selectedIkusuuLot?: ShiyoulotV2; // 帳票作成画面との受け渡し用
  selectedIkuseiLot?: ShiyoulotV2; // 帳票作成画面との受け渡し用
  selectedShukkaEvent?: ShukkaEvent; // 帳票作成画面との受け渡し用

  addedList: HimodukiLotInfo[] = []; // 注文明細と子Componentとの受け渡し用

  constructor(private db: AngularFirestore,
              private sessionService: SessionService,
              private commonService: CommonService,
              private messageService: MessageService,
              private masterService: MasterService,
              private shiyoulotService: ShiyoulotService,
              private docNumberKanriService: DocNumberKanriService,
              private dialog: MatDialog,) { }

  /**
   * CSVデータに関連するマスタの値のセット
   * および日付データのフォーマットをyyyy-MM-ddに変換
   * @param csvData
   */
  setValuesForCSV(csvData: ChumonCSVForIwamura[]) {
    console.log('setValuesForCSV start');
    csvData.forEach(csvRow => {
      this.setId(csvRow);
      this.convertDates(csvRow);
      this.setShukkaSakiId(csvRow);
      this.setKeishuId(csvRow);
    });
    console.log('setValuesForCSV end');
  }

  /**
   * 注文番号と鶏種(+雌雄)からDocumentIdを生成してセット
   * @param csvRow
   */
  setId(csvRow: ChumonCSVForIwamura) {
    if (csvRow.chumonNumber && csvRow.keishu && csvRow.osuMesu) {
      csvRow.id = csvRow.chumonNumber + csvRow.keishu + this.getShiyuu(csvRow.osuMesu);
    }
  }

  /**
   * Firestoreに保存するプロパティの値を
   * yyyy-MM-dd 形式に変換
   * @param csvRow
   */
  convertDates(csvRow: ChumonCSVForIwamura) {
    const dateProperties = ['edukeDate', 'otodokeJiki'] as const;
    dateProperties.forEach(propertyName => {
      const separator = '/';
      // separatorが含まれない、つまり変換済みとみなして処理を中断。
      if (csvRow[propertyName] && csvRow[propertyName].indexOf(separator) === -1) {
        return;
      }
      const ymdArray = csvRow[propertyName].split(separator);
      const tempDate = ymdArray[0].padStart(2, '0') + '-' + ymdArray[1].padStart(2, '0') + '-';
      if (propertyName === 'otodokeJiki') {
        csvRow.yoteiShukkaDate = tempDate + this.convertJunToDate(ymdArray[2]);
      } else {
        csvRow[propertyName] = tempDate + ymdArray[2].padStart(2, '0');
      }
    });
  }

  /**
   * otodokeJikiの末尾をddに変換
   * @param jun
   */
  convertJunToDate(jun: string) {
    return jun === '上旬' ? '01'
      : jun === '中旬' ? '11'
        : jun === '下旬' ? '21' : '';
  }

  /**
   * 「出荷先名」(顧客名)を元に出荷先Idをセット
   * @param csvRow
   */
  setShukkaSakiId(csvRow: ChumonCSVForIwamura) {
    if (csvRow.shukkaSakiName) {
      const shukkaSakiId
        = this.commonService.getValue<ShukkaSaki, string>(this.masterService.shukkaSakiMaster, 'name', csvRow.shukkaSakiName, 'id');
      if (shukkaSakiId) {
        csvRow.shukkaSakiId = shukkaSakiId;
      }
    }
  }

  /**
   * 「鶏種」と「雌雄」を元に「鶏種Id」を取得しセット
   * @param csvRow
   */
  setKeishuId(csvRow: ChumonCSVForIwamura) {
    if (csvRow.keishu && csvRow.osuMesu) {
      // 「鶏種」と「雌雄」を元に「鶏種Id」を取得
      const shiyuu = this.getShiyuu(csvRow.osuMesu);
      const meisai = this.masterService.keishuMaster.find(row => {
        const keishuRyakushou = row.keishuRyakushou.replace('♂', '');
        return keishuRyakushou === csvRow.keishu
          && row.shiyuu === shiyuu;
      });
      if (meisai) {
        csvRow.keishuId = meisai.id;
      }
    }
  }

  /**
   * OsuMesuをShiyuuに変換
   * オス → m
   * メス → f
   * @param osuMesu
   */
  getShiyuu(osuMesu: OsuMesu): Shiyuu {
    if (osuMesu === 'オス') {
      return 'm';
    }
    return 'f';
  }

  /**
   * ShiyuuをOsuMesuに変換
   * m → オス
   * f → メス
   * @param shiyuu
   */
  getOsuMesu(shiyuu: Shiyuu): OsuMesu {
    if (shiyuu === 'm') {
      return 'オス';
    }
    return 'メス';
  }

  /**
   * 注文Collectionへのリファレンスを返す
   */
  genChumonCollectionRef() : firebase.firestore.CollectionReference<firebase.firestore.DocumentData> {
    return this.genRootCollectionRef(COLLECTION_NAME.CHUMON);
  }

  /**
   * collectionName へのリファレンスを返す
   * @param collectionName
   */
  genRootCollectionRef(collectionName: string) : firebase.firestore.CollectionReference<firebase.firestore.DocumentData> {
    const customerId = this.sessionService.session.personalSetting.customerId;
    return this.db.firestore.collection(COLLECTION_NAME.CUSTOMERS).doc(customerId).collection(collectionName);
  }

  /**
   * 注文データ取得クエリを生成
   * @param kensakuKey
   */
  genChumonRefWithQuery(kensakuKey: ChumonKensakuKey = {}) {
    const customerId = this.sessionService.session.personalSetting.customerId;
    console.log('kensakuKey:', kensakuKey);
    return this.db.collection(COLLECTION_NAME.CUSTOMERS).doc(customerId)
      .collection(COLLECTION_NAME.CHUMON, ref => {
        let query: firestore.CollectionReference |
            firestore.Query = ref;
        if (kensakuKey.id) {
          query = query.where('id', '==', kensakuKey.id);
        }
        if (kensakuKey.chumonNumber) {
          query = query.where('chumonNumber', '==', kensakuKey.chumonNumber);
        }
        if (kensakuKey.shukkaSakiId) {
          query = query.where('shukkaSakiId', '==', kensakuKey.shukkaSakiId);
        }
        if (kensakuKey.keishuId) {
          query = query.where('keishuId', '==', kensakuKey.keishuId);
        }
        if (kensakuKey.edukeDate) {
          query = query.where('edukeDate', '==', kensakuKey.edukeDate);
        }
        if (kensakuKey.yoteiShukkaDateFrom) {
          query = query.where('yoteiShukkaDate', '>=', kensakuKey.yoteiShukkaDateFrom);
        }
        if (kensakuKey.yoteiShukkaDateTo) {
          query = query.where('yoteiShukkaDate', '<=', kensakuKey.yoteiShukkaDateTo);
        }
        if (kensakuKey.tantousha) {
          query = query.where('tantousha', '==', kensakuKey.tantousha);
        }
        if (kensakuKey.yoteiShukkaDateFrom && kensakuKey.yoteiShukkaDateTo) {
          query = query.orderBy('yoteiShukkaDate', 'desc');
        }
        query = query.limit(QUERY_LIMIT_CHUMON);
        return query;
      });
  }

  /**
   * 注文データをobservableとして取得
   * @param kensakuKey
   */
  getChumons(kensakuKey: ChumonKensakuKey) {
    return this.genChumonRefWithQuery(kensakuKey)
      .valueChanges({idField: 'id'}) as Observable<ChumonForIwamura[]>;
  }

  /**
   * 注文CSVデータ保存処理
   * @param csvData
   */
  saveChumonCSV(csvData: ChumonCSVForIwamura[]) {
    const batch = this.db.firestore.batch();
    const ref = this.genChumonCollectionRef();
    csvData.forEach(csvRow => {
      const doc = this.genChumonDocFromCsvRow(csvRow);
      if (doc.id) {
        batch.set(ref.doc(doc.id), doc, {merge: true});
      }
    });
    return batch.commit();
  }

  /**
   * 手入力された注文を新規追加する
   * 更新時は別メソッドを使用する想定
   * @param chumon
   */
  async saveNewManualChumon(chumon: ChumonForManualAdd) {
    try {
      const chumonRef = this.genChumonCollectionRef();
      await this.db.firestore.runTransaction(async(tx) => {
        const newDocNumber = await this.docNumberKanriService.getNewDocNumber(tx, DOCUMENT_NAME.CHUMON_NUMBER, chumonRef);
        chumon.id = MANUAL_CHUMON_PREFIX + newDocNumber;
        chumon.chumonNumber = newDocNumber;
        chumon.manualChumonNumber = newDocNumber;
        tx.set(this.genChumonCollectionRef().doc(chumon.id), chumon);
        this.docNumberKanriService.saveTargetDoc(tx, DOCUMENT_NAME.CHUMON_NUMBER, newDocNumber);
      });
    } catch (e) {
      console.log(e);
    }
  }

  /**
   * 注文Document及び関連する出荷イベントを更新
   * @param chumon
   * @param willDeleteHimodukiList
   */
  updateChumon(chumon: ChumonForIwamura, willDeleteHimodukiList: ShiyoulotKey[]) {
    const ref = this.genChumonCollectionRef();
    const batch = this.db.firestore.batch();
    this.batchSetToReleatedShukkaEvent(batch, chumon, willDeleteHimodukiList);
    batch.set(ref.doc(chumon.id), chumon, {merge: true});
    return batch.commit();
  }

  /**
   * 関連する出荷イベントに対し、紐付きを更新する
   * @param batch
   * @param chumon
   * @param willDeleteHimodukiList
   */
  batchSetToReleatedShukkaEvent(batch: firebase.firestore.WriteBatch, chumon: ChumonForIwamura,
    willDeleteHimodukiList: ShiyoulotKey[]) {
    if (!chumon.himodukiLotInfo || !chumon.himodukiLotInfo.length) { return; }
    const ref = this.genRootCollectionRef(COLLECTION_NAME.EVENT);
    chumon.himodukiLotInfo.forEach((himodukiLotInfo, index, object) => {
      const shiyoulotKey = Object.keys(himodukiLotInfo)[0];
      const shukkaEventId = Object.values(himodukiLotInfo)[0];
      if (willDeleteHimodukiList.includes(shiyoulotKey)) {
        // 紐付きを消す
        batch.set(ref.doc(shukkaEventId), {'himodukiId': ''}, {merge: true});
        // chumon.himodukiLotInfoからも消す
        object.splice(index, 1);
      } else {
        batch.set(ref.doc(shukkaEventId), {'himodukiId': chumon.id}, {merge: true});
      }
    });
  }

  /**
   * 注文Documentを削除
   * 呼び出し前に飼養ロットとの紐付きが無いことをチェックしている前提で
   * ここではチェックも紐付け先の更新も行わない
   * @param chumon
   */
  deleteChumon(chumon: ChumonForIwamura) {
    const ref = this.genChumonCollectionRef();
    return ref.doc(chumon.id).delete();
  }

  /**
   * 飼養ロット選択モーダルを開く
   * @param keishuId
   * @param edukeDate
   * @param selectedShukkaEventIds
   */
  openSelectShiyoulotModal(keishuId: string, edukeDate?: yyyyMMdd, selectedShukkaEventIds: (string | null | undefined)[] = []) {
    const dialogRef = this.dialog.open<SelectShiyoulotDialogComponent>(SelectShiyoulotDialogComponent, {
      data: {
        keishuId,
        edukeDate,
        selectedShukkaEventIds,
        keishaShubetsu: '',
        nyuukaHimodukiInfoList: []
      },
      maxWidth: '90vw',
      minWidth: '0vw',
      height: '90vh',
      panelClass: 'dialog-with-close', // dialog外クリックでdialogを閉じる。
    });

    return firstValueFrom(dialogRef.afterClosed()).then((result: SelectShiyoulotDialogResp) => {
      if (!result) { return; }
      return result;
    });
  }

  /**
   * himodukiLotInfoから出荷イベントのIdを取得
   * @param chumon
   */
  genSelectedShukkaEventIds(chumon: ChumonForIwamura) {
    if (!chumon.himodukiLotInfo || !chumon.himodukiLotInfo.length) { return []; }
    return Array.from(new Set(chumon.himodukiLotInfo.map(himodukiLotInfo => Object.values(himodukiLotInfo)[0])));
  }

  /**
   * 注文と飼養ロットをつなぐ出荷イベントを取得し、
   * selectedShukkaEventにセットする
   * @param himodukiLotInfo
   */
  async getSelectedShukkaEvent(himodukiLotInfo: HimodukiLotInfo) {
    const shukkaEventId = Object.values(himodukiLotInfo)[0];
    this.selectedShukkaEvent
      = (await this.genRootCollectionRef(COLLECTION_NAME.EVENT).doc(shukkaEventId).get()).data() as ShukkaEvent;
    console.log('shukkaEventId:', shukkaEventId, this.selectedShukkaEvent);
  }

  /**
   * 飼養ロット選択モーダルからの返り値をhimodukiLotInfoにセット
   * @param chumon
   * @param selectShiyoulotDialogResp
   */
  setHimodukiLotInfo(chumon: ChumonForIwamura, selectShiyoulotDialogResp: SelectShiyoulotDialogResp) {
    if (!selectShiyoulotDialogResp.shiyoulotId || !selectShiyoulotDialogResp.shukkaEvent) { return; }
    // 紐付けロットinfoをセット。
    // 飼養ロット登録時に紐付けロットinfoをセットし直す。
    const shiyoulotKey = selectShiyoulotDialogResp.shiyoulotId + SHIYOULOT_KEY_SUFFIX;
    if (selectShiyoulotDialogResp.shukkaEvent.id) {
      const himodukiLot: HimodukiLotInfo = { [shiyoulotKey]: selectShiyoulotDialogResp.shukkaEvent.id };
      if (chumon.himodukiLotInfo && chumon.himodukiLotInfo.length) {
        chumon.himodukiLotInfo.push(himodukiLot);
      } else {
        chumon.himodukiLotInfo = [himodukiLot];
      }
      this.addedList.push(himodukiLot);
    }
  }

  /**
   * HimodukiLotInfoからShiyoulotKeyを取り出す
   * @param himodukiLotInfo
   */
  getShiyoulotKey(himodukiLotInfo: HimodukiLotInfo): ShiyoulotKey {
    return this.commonService.keysOf(himodukiLotInfo)[0].toString();
  }

  /**
   * shiyoulotKeyからShiyoulotを取得し、
   * TreeNode型に変換して返す
   * @param shiyoulotKey
   */
  async getChild(shiyoulotKey: ShiyoulotKey): Promise<TreeNode> {
    const shiyoulotId = shiyoulotKey.replace(SHIYOULOT_KEY_SUFFIX, '');
    const shiyoulot = (await this.shiyoulotService.getShiyoulot(shiyoulotId)).data() as ShiyoulotV2;
    return Object.assign(shiyoulot, {children: [], toDelete: false});
  }

  /**
   * 配下の飼養ロットを取得しchildrenにセット
   * @param treeNode
   */
  async getChildren(treeNode: TreeNode) {
    const children: TreeNode[] = [];
    // 取得済みの場合、処理を中断
    if (treeNode.children.length) { return treeNode; }
    for (const himodukiLotInfo of treeNode.himodukiLotInfo) {
      children.push(await this.getChild(this.getShiyoulotKey(himodukiLotInfo)));
    }
    this.commonService.sortObjectArray(children, 'shiyoulotNumber');
    treeNode.children = children;
    console.log('treeNode', treeNode);
    return treeNode;
  }

  /**
   * ChumonCSVForIwamuraからFirestoreに保存するデータを取得しセット
   * @param csvRow
   */
  genChumonDocFromCsvRow(csvRow: ChumonCSVForIwamura): ChumonForIwamura {
    return {
      id: csvRow.id ? csvRow.id : '',
      bikou: csvRow.bikou ? csvRow.bikou : '',
      bikouA: csvRow.bikouA ? csvRow.bikouA : '',
      bikouB: csvRow.bikouB ? csvRow.bikouB : '',
      bikouC: csvRow.bikouC ? csvRow.bikouC : '',
      chumonNumber: csvRow.chumonNumber ? csvRow.chumonNumber : 0,
      edukeDate: csvRow.edukeDate ? csvRow.edukeDate : '',
      keishuId: csvRow.keishuId ? csvRow.keishuId : '',
      shukkaSakiId: csvRow.shukkaSakiId ? csvRow.shukkaSakiId : '',
      yoteiShukkaDate: csvRow.yoteiShukkaDate ? csvRow.yoteiShukkaDate : '',
      tantousha: csvRow.tantousha ? csvRow.tantousha : '',
      vaccineBikou: csvRow.vaccineBikou ? csvRow.vaccineBikou : '',
      vaccineProgram: csvRow.vaccineProgram ? csvRow.vaccineProgram : '',
      wasuu: csvRow.wasuu ? csvRow.wasuu : 0,
      chouhyouIdArray: [],
      himodukiLotInfo: [],
      memo: ''
    };
  }

  /**
   * 手入力用注文型のObjectを生成
   */
  genChumon(): ChumonForManualAdd {
    return {
      id: '',
      bikou: '',
      bikouA: '',
      bikouB: '',
      bikouC: '',
      chumonNumber: null,
      edukeDate: '',
      keishuId: null,
      shukkaSakiId: null,
      tantousha: '',
      wasuu: 0,
      yoteiShukkaDate: '',
      vaccineProgram: '',
      vaccineBikou: '',
      chouhyouIdArray: [],
      himodukiLotInfo: [],
      memo: ''
    };
  }

  /**
   * 餌付け日に応じて予定出荷日を更新
   * @param edukeDate
   * @param chumon
   */
  updateYoteiShukkaDate(edukeDate: unknown, chumon: ChumonForManualAdd) {
    if (chumon && typeof edukeDate === 'string') {
      chumon.yoteiShukkaDate = this.commonService.genOffsetDateWithStandardDateString(edukeDate, 112);
    }
  }

  /**
   * 各行にマスタの値がセットされているかをチェック
   * セットされていない行があれば不正とみなしfalseを返す
   * @param csvData
   */
  checkCSVDataIsValid(csvData: ChumonCSVForIwamura[]) {
    if (!csvData.length) { return false; }
    for (const csvRow of csvData) {
      if (!csvRow.keishuId || !csvRow.shukkaSakiId) {
        return false;
      }
    }
    return true;
  }
}
